import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { hardDeletePatientAPI } from 'services/profileServices';

const CONFIRM_TEXT = 'DELETE';

/**
 * Props for {@link HardDeleteDialog}.
 * open      - whether the dialog is shown
 * patientId - patient to permanently delete
 * onClose   - called when the dialog is dismissed without deleting
 * onDeleted - called after the server confirms the delete
 */
interface HardDeleteDialogProps {
  open: boolean;
  patientId: number;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * MM-1106: Admin / Tech VMA only confirmation dialog for permanently deleting a patient
 * account. The admin must type DELETE to enable the confirm button. Server errors
 * are shown as a toast.
 */
const HardDeleteDialog: React.FC<HardDeleteDialogProps> = ({ open, patientId, onClose, onDeleted }) => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  /**
   * Resets the typed confirmation and closes the dialog, unless a delete is in flight.
   */
  const handleClose = () => {
    if (loading) return;
    setConfirmText('');
    onClose();
  };

  /**
   * Calls the hard delete API and reports the outcome to the admin.
   */
  const handleDelete = () => {
    setLoading(true);
    hardDeletePatientAPI(patientId)
      .then((response: any) => {
        if (response?.status === 200) {
          // MM-1132: one message after every successful delete, including a partial clean-up
          // (remaining services are retried automatically in the background).
          toast.success('Patient permanently deleted.');
          setConfirmText('');
          onDeleted();
        }
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMsg ??
          error?.response?.data?.message ??
          (typeof error?.response?.data === 'string' ? error.response.data : null) ??
          'Unable to delete the patient account. Please try again.';
        toast.error(message);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      // Smaller side margins on phones so the dialog has more room
      sx={{ '& .MuiDialog-paper': { mx: { xs: 2, sm: 4 } } }}
    >
      {/* Centred title; smaller with a normal line height on phones so it does not wrap awkwardly */}
      <DialogTitle
        sx={{
          color: '#d32f2f',
          fontWeight: 600,
          textAlign: 'center',
          fontSize: { xs: 18, sm: 24 },
          lineHeight: { xs: 1.4, sm: 3.2 },
          pt: { xs: 3, sm: 2 },
        }}
      >
        {/* MM-1134: pop-up text as per story */}
        Permanently Delete Patient Account?
      </DialogTitle>
      <DialogContent>
        <Typography mb={1.5}>
          This permanently deletes the selected patient’s account from the MindWeal platform.{' '}
          <strong>This cannot be undone.</strong>
        </Typography>
        <Typography component='ul' sx={{ pl: 2.5, mb: 2, '& li': { mb: 0.5 } }}>
          <li>
            If this is the only patient linked to the guardian’s login, the guardian’s login will also be deleted.
          </li>
          <li>
            If multiple patients are linked, only the selected patient’s account will be deleted. The guardian’s
            login and other patient accounts will remain.
          </li>
          <li>DrChrono records and HubSpot information will not be removed.</li>
          <li>If the patient returns for services, they must register and complete new patient onboarding again.</li>
        </Typography>
        <Typography mb={1}>
          Type <strong>{CONFIRM_TEXT}</strong> to confirm.
        </Typography>
        <TextField
          fullWidth
          size='small'
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={loading}
          autoFocus
          inputProps={{ 'aria-label': 'Type DELETE to confirm' }}
        />
      </DialogContent>
      {/* Buttons centred; on phones stacked full width with Delete on top */}
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          justifyContent: 'center',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1.5, sm: 2 },
          '& > :not(:first-of-type)': { ml: 0 },
        }}
      >
        <Button
          variant='outlined'
          onClick={handleClose}
          disabled={loading}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 140 } }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={handleDelete}
          disabled={confirmText !== CONFIRM_TEXT || loading}
          startIcon={loading ? <CircularProgress size={16} color='inherit' /> : null}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 180 } }}
        >
          Permanently Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HardDeleteDialog;
